import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom';
import { PageHeader } from '../../components/ui/PageHeader';
import { PageNotificationToast } from '../../components/notifications/PageNotificationToast';
import { PricingUpdateForm } from '../../components/forms/PricingUpdateForm';
import { usePageNotifications } from '../../hooks/usePageNotifications';
import { pageFeedbackToToast } from '../../lib/pageFeedbackToast';
import { formatSubmittedAt, statusClass, STATUS_LABEL } from '../../lib/accountCreationUi';
import { formatDisplayPersonName } from '../../lib/formatPersonName';
import {
  formatPricingUpdateNotifySummary,
  notifyPricingUpdateDecision,
  notifyPricingUpdatePendingApproval,
  notifyPricingUpdateReturned,
} from '../../lib/pricingUpdateNotifications';
import {
  approvePricingUpdate,
  fetchPricingUpdateSubmission,
  getPricingUpdateAttachmentSignedUrl,
  normalizePricingUpdateFormData,
  rejectPricingUpdate,
  requirePricingUpdateDecisionReason,
  startPricingUpdateProcessing,
  submitPricingUpdateForApproval,
  updatePricingUpdateAuthorised,
  uploadPricingUpdateAttachment,
  validatePricingUpdateAuthorised,
} from '../../lib/pricingUpdateSubmissions';
import { fetchMyPricingUpdateCapabilities } from '../../lib/pricingUpdateRoles';
import { useAuth } from '../../contexts/AuthContext';
import { usePortal } from '../../contexts/PortalContext';
import { useFormsSubmissionsRealtime } from '../../hooks/useFormsSubmissionsRealtime';
import {
  EMPTY_PRICING_UPDATE_FORM,
  type ApplicationSubmission,
  type PricingUpdateAttachmentRef,
  type PricingUpdateFormData,
} from '../../types/applicationForm';
import type { PricingUpdateNewNavigateState } from './PricingUpdateNewPage';

function personLabel(raw: string | null | undefined): string {
  return formatDisplayPersonName(raw) || String(raw ?? '').trim();
}

export function PricingUpdateDetailPage() {
  const { id = '' } = useParams();
  const location = useLocation();
  const navigate = useNavigate();
  const { basePath, kind } = usePortal();
  const isDem = kind === 'dem';
  const pageClass = `af-applications-page af-form-page${isDem ? ' puf-page--floating' : ''}`;
  const { profile, authUser } = useAuth();

  const listPath = basePath ? `${basePath}/pricing-update` : '/pricing-update';

  const [row, setRow] = useState<ApplicationSubmission | null>(null);
  const [form, setForm] = useState<PricingUpdateFormData>(EMPTY_PRICING_UPDATE_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState<string | null>(null);
  const [isAuthorised, setIsAuthorised] = useState(false);
  const [isApprover, setIsApprover] = useState(false);
  const [pendingAuthorisedFiles, setPendingAuthorisedFiles] = useState<File[]>([]);
  const [startedProcessing, setStartedProcessing] = useState(false);
  const lastErrorToastRef = useRef<string | null>(null);
  const lastNoticeToastRef = useRef<string | null>(null);
  const shownStatusTipsRef = useRef<Set<string>>(new Set());
  const { push: pushToast, activeToast, dismissToast } = usePageNotifications();

  const actorEmail = authUser?.email ?? profile?.email ?? null;

  useEffect(() => {
    if (error && error !== lastErrorToastRef.current) {
      lastErrorToastRef.current = error;
      pushToast(pageFeedbackToToast({ kind: 'error', message: error }));
    }
    if (!error) lastErrorToastRef.current = null;
  }, [error, pushToast]);

  useEffect(() => {
    if (notice && notice !== lastNoticeToastRef.current) {
      lastNoticeToastRef.current = notice;
      pushToast(pageFeedbackToToast({ kind: 'success', message: notice }));
    }
    if (!notice) lastNoticeToastRef.current = null;
  }, [notice, pushToast]);

  useEffect(() => {
    const flash = (location.state ?? null) as PricingUpdateNewNavigateState | null;
    if (!flash?.pufNotice && !flash?.pufError) return;
    if (flash.pufNotice) setNotice(flash.pufNotice);
    if (flash.pufError) setError(flash.pufError);
    navigate(location.pathname, { replace: true, state: null });
  }, [location.pathname, location.state, navigate]);

  const applyLoaded = (data: ApplicationSubmission) => {
    setRow(data);
    setForm(normalizePricingUpdateFormData(data.form_data));
  };

  const load = useCallback(async () => {
    if (!id) return;
    setLoading(true);
    setError(null);
    try {
      const [data, caps] = await Promise.all([
        fetchPricingUpdateSubmission(id),
        fetchMyPricingUpdateCapabilities(actorEmail),
      ]);
      applyLoaded(data);
      setIsAuthorised(caps.isAuthorised);
      setIsApprover(caps.isApprover);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to load.');
      setRow(null);
    } finally {
      setLoading(false);
    }
  }, [id, actorEmail]);

  useEffect(() => {
    void load();
  }, [load]);

  useFormsSubmissionsRealtime({
    enabled: Boolean(id),
    tables: ['pricing_update_submissions'],
    filter: id ? `id=eq.${id}` : undefined,
    onChange: () => {
      void load();
    },
  });

  // Authorised user opening a New request → Processing
  useEffect(() => {
    if (!row || !isAuthorised || startedProcessing) return;
    if (row.status !== 'new') return;
    let cancelled = false;
    setStartedProcessing(true);
    void (async () => {
      try {
        const updated = await startPricingUpdateProcessing(row.id);
        if (!cancelled) applyLoaded(updated);
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to start processing.');
        }
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [row, isAuthorised, startedProcessing]);

  const status = row?.status;
  const authorisedEditable =
    isAuthorised && (status === 'new' || status === 'processing');
  const canSubmitToApprover = isAuthorised && (status === 'new' || status === 'processing');
  /** Approver reject on pending_approval returns the form to Authorised (processing). */
  const canApproverReturn = isApprover && status === 'pending_approval';
  /**
   * Authorised terminal reject / cancel.
   * On pending_approval, Approvers use Return instead — if the actor is also Authorised,
   * hide terminal Reject so the shared RPC does not bounce when they meant to cancel.
   */
  const canAuthorisedReject =
    isAuthorised &&
    (status === 'new' ||
      status === 'processing' ||
      (status === 'pending_approval' && !isApprover));
  const canApprove = isApprover && status === 'pending_approval';

  const isReturnedByApprover =
    status === 'processing' && Boolean(row?.rejected_by || row?.reject_reason);

  useEffect(() => {
    if (!row) return;
    let tip: { kind: 'info' | 'error'; title: string; message: string } | null = null;
    if (isReturnedByApprover) {
      tip = {
        kind: 'info',
        title: 'Returned by Approver',
        message: `${row.rejected_by ? `By ${personLabel(row.rejected_by)}. ` : ''}${
          row.reject_reason ? `${row.reject_reason} ` : ''
        }Authorised users can edit and submit again.`,
      };
    } else if (status === 'rejected' && (row.reject_reason || row.rejected_by)) {
      tip = {
        kind: 'error',
        title: 'Rejected',
        message: `${row.rejected_by ? `By ${personLabel(row.rejected_by)}. ` : ''}${
          row.reject_reason || 'This pricing update was rejected.'
        }`,
      };
    } else if (status === 'approved') {
      tip = {
        kind: 'info',
        title: 'Approved',
        message: row.approved_by
          ? `By ${personLabel(row.approved_by)}. The form is locked.`
          : 'The form is locked.',
      };
    } else if (status === 'pending_approval') {
      tip = {
        kind: 'info',
        title: 'Pending approval',
        message: 'Waiting for Approver decision.',
      };
    }
    if (!tip) return;
    const key = `${row.id}:${tip.title}:${row.reject_reason ?? ''}:${row.rejected_at ?? ''}`;
    if (shownStatusTipsRef.current.has(key)) return;
    shownStatusTipsRef.current.add(key);
    pushToast(pageFeedbackToToast(tip));
  }, [
    row,
    status,
    isReturnedByApprover,
    pushToast,
  ]);

  const workflowItems = useMemo(() => {
    if (!row) return [] as { label: string; name: string; at: string | null }[];
    const items: { label: string; name: string; at: string | null }[] = [];
    const submitter = personLabel(row.submitted_by) || personLabel(row.submitter_email);
    if (submitter) {
      items.push({ label: 'Submitted by', name: submitter, at: row.submitted_at });
    }
    if (row.authorised_by) {
      items.push({
        label: 'Updated by',
        name: personLabel(row.authorised_by),
        at: row.authorised_at ?? null,
      });
    }
    if (row.approved_by) {
      items.push({
        label: 'Approved by',
        name: personLabel(row.approved_by),
        at: row.approved_at ?? null,
      });
    }
    if (row.rejected_by && (status === 'rejected' || isReturnedByApprover)) {
      items.push({
        label: status === 'rejected' ? 'Rejected by' : 'Returned by',
        name: personLabel(row.rejected_by),
        at: row.rejected_at ?? null,
      });
    }
    return items;
  }, [row, status, isReturnedByApprover]);

  const uploadPendingAuthorised = async (submissionId: string, base: PricingUpdateFormData) => {
    if (pendingAuthorisedFiles.length === 0) return base;
    const uploaded = [...base.authorised_attachments];
    for (const file of pendingAuthorisedFiles) {
      uploaded.push(await uploadPricingUpdateAttachment(submissionId, 'authorised', file));
    }
    setPendingAuthorisedFiles([]);
    return normalizePricingUpdateFormData({ ...base, authorised_attachments: uploaded });
  };

  const handleSave = async () => {
    if (!row || !authorisedEditable) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const withFiles = await uploadPendingAuthorised(row.id, form);
      const updated = await updatePricingUpdateAuthorised(row.id, withFiles);
      applyLoaded(updated);
      setNotice('Saved.');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save.');
    } finally {
      setBusy(false);
    }
  };

  const handleSubmitToApprover = async () => {
    if (!row || !canSubmitToApprover) return;
    const validationError = validatePricingUpdateAuthorised(form);
    if (validationError) {
      setError(validationError);
      return;
    }
    if (!window.confirm('Submit this pricing update to the approver?')) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const withFiles = await uploadPendingAuthorised(row.id, form);
      const updated = await submitPricingUpdateForApproval(row.id, withFiles);
      applyLoaded(updated);
      const notifyResult = await notifyPricingUpdatePendingApproval(updated);
      const summary = await formatPricingUpdateNotifySummary(notifyResult);
      setNotice(`Submitted for approval. ${summary}`);
      if (!notifyResult.ok || notifyResult.sent.length === 0) {
        setError(
          'Check Approver emails in Forms settings → Pricing Update so they get notified.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to submit for approval.');
    } finally {
      setBusy(false);
    }
  };

  const decisionReasonOrAlert = (): string | null => {
    const msg = requirePricingUpdateDecisionReason(form);
    if (!msg) return null;
    setError(msg);
    return msg;
  };

  const handleApprove = async () => {
    if (!row || !canApprove) return;
    if (decisionReasonOrAlert()) return;
    if (!window.confirm('Approve this pricing update?')) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await approvePricingUpdate(row.id, form);
      applyLoaded(updated);
      const notifyResult = await notifyPricingUpdateDecision(updated, 'approved');
      const summary = await formatPricingUpdateNotifySummary(notifyResult);
      setNotice(`Approved. ${summary}`);
      if (!notifyResult.ok || notifyResult.sent.length === 0) {
        setError(
          'The submitter may not be notified until their email is on the submission.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to approve.');
    } finally {
      setBusy(false);
    }
  };

  const handleApproverReturn = async () => {
    if (!row || !canApproverReturn) return;
    if (decisionReasonOrAlert()) return;
    if (
      !window.confirm(
        'Return this request to Authorised users for changes? They can edit and submit again.',
      )
    ) {
      return;
    }
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      const updated = await rejectPricingUpdate(
        row.id,
        form.reason_decision.trim(),
        form,
      );
      applyLoaded(updated);
      const notifyResult = await notifyPricingUpdateReturned(updated);
      const summary = await formatPricingUpdateNotifySummary(notifyResult);
      setNotice(`Returned to Authorised users. ${summary}`);
      if (!notifyResult.ok || notifyResult.sent.length === 0) {
        setError(summary);
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to return request.');
    } finally {
      setBusy(false);
    }
  };

  const handleAuthorisedReject = async () => {
    if (!row || !canAuthorisedReject) return;
    if (decisionReasonOrAlert()) return;
    if (!window.confirm('Reject this pricing update permanently?')) return;
    setBusy(true);
    setError(null);
    setNotice(null);
    try {
      let payload = form;
      if (authorisedEditable) {
        payload = await uploadPendingAuthorised(row.id, form);
        await updatePricingUpdateAuthorised(row.id, payload);
      }
      const updated = await rejectPricingUpdate(
        row.id,
        payload.reason_decision.trim(),
        payload,
      );
      applyLoaded(updated);
      const notifyResult = await notifyPricingUpdateDecision(updated, 'rejected');
      const summary = await formatPricingUpdateNotifySummary(notifyResult);
      setNotice(`Rejected. ${summary}`);
      if (!notifyResult.ok || notifyResult.sent.length === 0) {
        setError(
          'The submitter may not be notified until their email is on the submission.',
        );
      }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reject.');
    } finally {
      setBusy(false);
    }
  };

  const handleOpenAttachment = async (ref: PricingUpdateAttachmentRef) => {
    try {
      const url = await getPricingUpdateAttachmentSignedUrl(ref.path);
      window.open(url, '_blank', 'noopener,noreferrer');
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to open attachment.');
    }
  };

  const handleRemoveSavedAttachment = (kind: 'requestor' | 'authorised', path: string) => {
    if (kind !== 'authorised' || !authorisedEditable) return;
    setForm((prev) =>
      normalizePricingUpdateFormData({
        ...prev,
        authorised_attachments: prev.authorised_attachments.filter((item) => item.path !== path),
      }),
    );
  };

  if (loading) {
    return (
      <div className={pageClass}>
        <p className="page-lead">Loading…</p>
      </div>
    );
  }

  if (!row) {
    return (
      <div className={pageClass}>
        {activeToast
          ? createPortal(
              <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
              document.body,
            )
          : null}
        {isDem ? (
          <>
            <Link to={listPath} className="btn-secondary delivery-page__back">
              Back to list
            </Link>
            <p className="alert alert-error">{error ?? 'Submission not found.'}</p>
          </>
        ) : (
          <PageHeader
            title="Pricing Update"
            description={error ?? 'Submission not found.'}
            actions={
              <Link to={listPath} className="btn-secondary btn-sm">
                Back to list
              </Link>
            }
          />
        )}
      </div>
    );
  }

  return (
    <div className={pageClass}>
      {isDem ? (
        <div className="dem-floating-page-actions dem-blanket-detail__toolbar">
          <div className="dem-blanket-detail__toolbar-start">
            <Link to={listPath} className="btn-secondary">
              Back to list
            </Link>
            <span className={statusClass(row.status)}>{STATUS_LABEL[row.status]}</span>
          </div>
        </div>
      ) : (
        <PageHeader
          title={`${row.application_no} - ${STATUS_LABEL[row.status]}`}
          description={`${row.company_name || form.customer_name || 'Pricing update'} · ${formatSubmittedAt(row.submitted_at)}`}
          actions={
            <div className="puf-page__header-actions">
              <span className={statusClass(row.status)}>{STATUS_LABEL[row.status]}</span>
              <Link to={listPath} className="btn-secondary btn-sm">
                Back to list
              </Link>
            </div>
          }
        />
      )}

      {activeToast
        ? createPortal(
            <PageNotificationToast notification={activeToast} onDismiss={dismissToast} />,
            document.body,
          )
        : null}

      <div className="puf-detail-layout">
        <div className="puf-detail-main">
          <PricingUpdateForm
            mode="detail"
            value={form}
            onChange={setForm}
            requestorEditable={false}
            authorisedEditable={authorisedEditable}
            decisionReasonEditable={
              authorisedEditable || canApprove || canApproverReturn || canAuthorisedReject
            }
            decisionReasonRequired={
              canApprove || canApproverReturn || canAuthorisedReject
            }
            showAdminSection
            pendingAuthorisedFiles={pendingAuthorisedFiles}
            onPendingAuthorisedFilesChange={
              authorisedEditable ? setPendingAuthorisedFiles : undefined
            }
            onOpenAttachment={handleOpenAttachment}
            onRemoveSavedAttachment={
              authorisedEditable ? handleRemoveSavedAttachment : undefined
            }
          />

          <div className="puf-form__actions puf-form__actions--bar">
            {authorisedEditable ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => void handleSave()}
              >
                {busy ? 'Working…' : 'Save'}
              </button>
            ) : null}
            {canSubmitToApprover ? (
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={() => void handleSubmitToApprover()}
              >
                {busy ? 'Working…' : 'Submit to Approver'}
              </button>
            ) : null}
            {canApprove ? (
              <button
                type="button"
                className="btn-primary"
                disabled={busy}
                onClick={() => void handleApprove()}
              >
                {busy ? 'Working…' : 'Approve'}
              </button>
            ) : null}
            {canApproverReturn ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => void handleApproverReturn()}
              >
                {busy ? 'Working…' : 'Return to Authorised'}
              </button>
            ) : null}
            {canAuthorisedReject ? (
              <button
                type="button"
                className="btn-secondary"
                disabled={busy}
                onClick={() => void handleAuthorisedReject()}
              >
                {busy ? 'Working…' : 'Reject'}
              </button>
            ) : null}
          </div>
        </div>

        {workflowItems.length > 0 ? (
          <aside className="card puf-workflow-meta">
            <h3 className="puf-workflow-meta__title">Workflow</h3>
            <ul className="puf-workflow-meta__list">
              {workflowItems.map((item) => (
                <li key={`${item.label}-${item.name}-${item.at ?? ''}`}>
                  <span className="puf-workflow-meta__label">{item.label}</span>
                  <span className="puf-workflow-meta__name">{item.name}</span>
                  {item.at ? (
                    <span className="puf-workflow-meta__time">{formatSubmittedAt(item.at)}</span>
                  ) : null}
                </li>
              ))}
            </ul>
          </aside>
        ) : null}
      </div>
    </div>
  );
}
